// @flow
import pipe from 'ramda/es/pipe';
import evolve from 'ramda/es/evolve';
import map from 'ramda/es/map';
import prop from 'ramda/es/prop';
import partition from 'ramda/es/partition';
import propEq from 'ramda/es/propEq';
import unnest from 'ramda/es/unnest';
import reduce from 'ramda/es/reduce';
import assoc from 'ramda/es/assoc';
import filter from 'ramda/es/filter';
import merge from 'ramda/es/merge';
import keys from 'ramda/es/keys';
import cond from 'ramda/es/cond';
import append from 'ramda/es/append';
import join from 'ramda/es/join';
import lensProp from 'ramda/es/lensProp';
import over from 'ramda/es/over';
import curry from 'ramda/es/curry';
import path from 'ramda/es/path';
import T from 'ramda/es/T';
import without from 'ramda/es/without';

import i18n from 'app/i18n';
import { TARIFF_BUNDLE } from 'app/common/constants/tariffs';
import type { TariffType } from 'app/components/Tariff/Tariff';


const serviceName = srv => i18n.t(`subs:${srv}`, { context: 'short' });

const replaceTitle = (obj) => {
  const { service: srv } = obj;
  return over(lensProp('title'), () => `${i18n.t('tariff:sms_notification_by_service', { serviceName: serviceName(srv) })}:`, obj);
};

const servicesToTitles = map(srv => i18n.t('tariff:service_to_title', { serviceName: serviceName(srv) }));

const buildWarningOnSubscribe = (willConnectTitles, willDisconnectTitles) => {
  const willConnectString = join(', ', willConnectTitles);
  const willDisconnectString = join(', ', willDisconnectTitles);

  if (!willConnectString && !willDisconnectString) return '';

  return [
    willConnectTitles.length > 1 // eslint-disable-line no-nested-ternary
      ? i18n.t('tariff:tariff_pack_change_warning_part1_plural', { willConnectString })
      : (willConnectString ? i18n.t('tariff:tariff_pack_change_warning_part1', { willConnectString }) : ''),
    willDisconnectTitles.length > 1 // eslint-disable-line no-nested-ternary
      ? i18n.t('tariff:tariff_pack_change_warning_part2_plural', { willDisconnectString })
      : (willDisconnectString ? i18n.t('tariff:tariff_pack_change_warning_part2', { willDisconnectString }) : ''),
    i18n.t('tariff:tariff_pack_change_warning_part3'),
  ].join(' ');
};

type ActiveServices = {
  [service: string]: {
    subId: number,
    tariffId: number,
    tariffBundleId: number,
  }
}

const getHaveToBeDisconnectedServices = (
  servicesToConnect: Array<string>,
  activeServices: ActiveServices
): Array<string> => {
  const servicesObj = reduce((acc, serv) => {
    const tariffBundleId = path([serv, 'tariffBundleId'], activeServices);
    if (tariffBundleId) {
      const toRemoveServices = filter((servObj) => {
        if (!servObj) return false;
        return propEq('tariffBundleId', tariffBundleId, servObj);
      }, activeServices);

      return { ...acc, ...toRemoveServices };
    }

    return acc;
  }, {}, servicesToConnect);

  return keys(servicesObj);
};

/**
* Преобразование пакетного тарифа
* Композиция тарифов в массиве по их id в этом же массиве
* (меняем id на сами тарифы в массиве)
*/

type Rt = (tariff: TariffType) => TariffType;

const composePackTariff = (
  bundlesTariffs: Object, // объект тарифов из состояния { id: тариф... }
  // state.apps.tariffs
  // state.finances.tariffs.bundles - для списка тарифов в "Выберите тариф"
  currentService: string,
  activeServices: ActiveServices // активные сервисы текущего пользователя сервис: id (use selectActiveServices)
  // subs: Object
): Rt => pipe(
  // поиск и сортировка (тaриф с текущим сервисом первый) тарифов
  evolve({
    tariff_ids: pipe(
      map(id => pipe(
        prop(id),
        replaceTitle
      )(bundlesTariffs)),
      partition(propEq('service', currentService)),
      unnest
    ),
  }),
  // переименование поля 'tariff_ids' -> 'inside'
  obj => reduce(
    (acc, key) => assoc(key === 'tariff_ids' ? 'inside' : key, obj[key], acc),
    {},
    keys(obj)
  ),
  (obj) => {
    const tariffServicesArray = map(({ service }) => service, prop('inside', obj));

    // Имена сервисов, которые будут подключены (Array)
    const servicesTitles = servicesToTitles(tariffServicesArray);
    // Имена сервисов, которые будут подключены с заменой текущих (Array)
    // Не учитывается, если нет подписки на сервис -- не будет отключаться
    const willConnectTitles = pipe(
      filter(srv => (srv !== currentService && activeServices[srv])),
      servicesToTitles
    )(tariffServicesArray);

    // обходим все сервисы, которые будут подключаться
    // и если это пакетный тариф, то добавить ВСЕ ЕГО сервисы в отключаемые
    const servicesToRemove = getHaveToBeDisconnectedServices(tariffServicesArray, activeServices);

    // // Уже подключенные сервисы (Array)
    // const activeServicesArray = keys(filter(identity, activeServices));
    // // Сервисы, которые будут отключены, если выбрать  текущий тариф (Array)
    const willDisconnect = without(tariffServicesArray, servicesToRemove);

    // Имена сервисов, которые будут отключены (Array)
    const willDisconnectTitles = servicesToTitles(willDisconnect);

    return merge(obj, {
      key: -obj.id,
      kind: TARIFF_BUNDLE,
      description: cond([
        [() => !!obj.description, append(obj.description)],
        [T, arr => arr[0]],
      ])([i18n.t('tariff:tariff_pack_description', { servicesTitles: join(', ', servicesTitles) })]),
      warn: buildWarningOnSubscribe(willConnectTitles, willDisconnectTitles),
    });
  }
);

export default curry(composePackTariff);
